import { assetsUrl } from "@api";

// Eén plek voor alle afbeeldings-URL's van producten, recepten en categorieën.
//
// De API stuurt per item een `image_version` mee (12 hex-tekens, uniek per
// schrijfactie in de bucket, ook na terugzetten). Die hangen we als ?v=<versie>
// aan de URL: een nieuwe foto heeft een nieuwe versie en dus een nieuwe URL.
// Caddy laat URL's met zo'n versie daarom een jaar cachen (immutable) en de app
// (expo-image) ziet een nieuwe foto toch meteen, zodra de productlijst opnieuw
// geladen is. Elke andere ?v= (zoals de oude ?v=3) krijgt maar een uur.
//
// Zonder versie (oude backend, of een item zonder foto) blijft de URL precies
// zoals hij was.

export type ImageKind = "product" | "recipe" | "category";

const prefixes: Record<ImageKind, string> = {
  product: "/images/products/",
  recipe: "/images/recepten/",
  category: "/images/categories/",
};

// categorySlug spiegelt de server (imagever.CategorySlug): kleine letters, zonder
// spaties, haakjes en &.
export function categorySlug(name: string | undefined | null): string {
  return (name || "").toLowerCase().replaceAll(" ", "").replaceAll("(", "").replaceAll(")", "").replaceAll("&", "");
}

// imageUrl bouwt de URL. key is de pid (product), de bestandsnaam uit het recept
// (recipe) of de slug (category).
export function imageUrl(kind: ImageKind, key: string | number, version?: string | null): string {
  const file = kind === "recipe" ? `${key}` : `${key}.jpg`;
  const query = version ? `?v=${encodeURIComponent(version)}` : "";
  return assetsUrl + prefixes[kind] + file + query;
}

// De versies van één foto uit de API. variants_version is gelijk aan
// image_version als de varianten van precies die foto er zijn (zie onder).
// thumbhash is de ThumbHash (https://evanw.github.io/thumbhash/) van precies
// die foto: CustomImage tekent er een wazige placeholder mee tot de foto
// binnen is. De server stuurt hem alleen mee als hij bij de huidige
// image_version hoort; zonder (oude backend, nog niet berekend) geen
// placeholder, zoals voorheen.
export type ImageVersions = { image_version?: string; variants_version?: string; thumbhash?: string };

// Versies per pid uit /products/v2/all, voor de plekken die alleen een pid
// kennen (lijstjes, bestelgeschiedenis, receptingrediënten). Gevuld door
// useAppData zodra de productlijst binnen is.
let productVersions = new Map<number, ImageVersions>();

export function setProductImageVersions(products: ({ pid: number } & ImageVersions)[] | undefined) {
  const next = new Map<number, ImageVersions>();
  products?.forEach((p) => {
    if (p?.image_version) {
      next.set(p.pid, { image_version: p.image_version, variants_version: p.variants_version, thumbhash: p.thumbhash });
    }
  });
  productVersions = next;
}

// versionsFor: de versies van het product zelf, anders die uit de productlijst.
function versionsFor(pid: number | string, own?: ImageVersions | null): ImageVersions {
  return own?.image_version ? own : productVersions.get(Number(pid)) || {};
}

// productImageUrl: met de versie van het product zelf, anders die uit de
// productlijst.
export function productImageUrl(pid: number | string, version?: string | null): string {
  return imageUrl("product", pid, version || productVersions.get(Number(pid))?.image_version);
}

// categoryImageUrl: zonder versie van de server blijft de oude handmatige
// cache-buster (?v=3) staan, zodat er niets terugvalt naar een foto die nog van
// vóór ?v=3 in een cache zit. Caddy ziet ?v=3 niet als versie (geen 12 hex-
// tekens) en geeft hem een uur, dus een vervangen foto komt ook dan door.
export function categoryImageUrl(name: string, version?: string | null): string {
  return imageUrl("category", categorySlug(name), version || "3");
}

// recipeImageUrl: de bestandsnaam staat in recipe.image.
export function recipeImageUrl(recipe: { image: string; image_version?: string }): string {
  return imageUrl("recipe", recipe.image, recipe.image_version);
}

// Varianten: naast elk origineel zet de server verkleinde versies neer, met de
// versie van het origineel waaruit ze gemaakt zijn in de naam:
//
//   /images/_v/<maat>/<map>/<bestand>.<versie>.<avif|jpg>?v=<versie>
//
// 360 voor lijsten en tegels (3x voor tot 120 pt), 1600 voor de grote foto's
// (het origineel tot hooguit 1600 px: productdetail, recepten).
//
// De server maakt ze op de achtergrond (na een upload, of via de timer). De API
// zegt per item of ze er zijn: variants_version is gelijk aan image_version als
// er varianten van precies díe foto zijn. Alleen dan vragen we ze op, anders
// het origineel. Zo zie je nooit een variant van een oude foto, en na een
// upload eerst het (nieuwe) origineel tot de varianten klaar zijn. Mislukt een
// variant toch, dan valt CustomImage terug: AVIF, JPEG-variant, origineel.
// Oude app-versies vragen gewoon het origineel en merken hier niets van.

export type ImageSize = 360 | 1600;

export type ImageCandidates = {
  avif: string;
  jpeg: string;
  original: string;
  // De placeholder (zie ImageVersions); hoort bij dezelfde versie als de URL's.
  thumbhash?: string;
};

export type ImageSource = { uri: string; thumbhash?: string } | ImageCandidates;

const variantDirs: Record<ImageKind, string> = {
  product: "products",
  recipe: "recepten",
  category: "categories",
};

function variantUrl(kind: ImageKind, key: string | number, size: ImageSize, ext: "avif" | "jpg", version: string): string {
  // Het bestand met extensie, zoals in de bucket (soep.jpg en soep.png hebben
  // elk hun eigen varianten).
  const file = kind === "recipe" ? `${key}` : `${key}.jpg`;
  const v = encodeURIComponent(version);
  return `${assetsUrl}/images/_v/${size}/${variantDirs[kind]}/${file}.${v}.${ext}?v=${v}`;
}

// imageCandidates geeft de bron voor (kind, key, versies, maat): de varianten
// in terugvalvolgorde als ze er zijn (variants_version == image_version),
// anders alleen het origineel. originalVersion is de ?v= van het origineel
// (bij categorieën met de ?v=3-terugval). De thumbhash gaat alleen mee met een
// image_version: zonder versie weten we niet bij welke foto hij hoort.
export function imageCandidates(
  kind: ImageKind,
  key: string | number,
  versions: ImageVersions,
  size: ImageSize,
  originalVersion: string | null | undefined = versions.image_version
): ImageSource {
  const original = imageUrl(kind, key, originalVersion);
  const version = versions.image_version;
  const placeholder = version && versions.thumbhash ? { thumbhash: versions.thumbhash } : {};
  if (!version || versions.variants_version !== version) {
    return { uri: original, ...placeholder };
  }
  return {
    avif: variantUrl(kind, key, size, "avif", version),
    jpeg: variantUrl(kind, key, size, "jpg", version),
    original,
    ...placeholder,
  };
}

// productImageCandidates: met de versies van het product zelf, anders die uit
// de productlijst (favorieten, lijstjes).
export function productImageCandidates(
  pid: number | string,
  product: ImageVersions | null | undefined,
  size: ImageSize
): ImageSource {
  return imageCandidates("product", pid, versionsFor(pid, product), size);
}

// categoryImageCandidates: zelfde ?v=3-terugval als categoryImageUrl.
export function categoryImageCandidates(name: string, category: ImageVersions, size: ImageSize): ImageSource {
  return imageCandidates("category", categorySlug(name), category, size, category.image_version || "3");
}

export function recipeImageCandidates(recipe: { image: string } & ImageVersions, size: ImageSize): ImageSource {
  return imageCandidates("recipe", recipe.image, recipe, size);
}

// candidateList zet de kandidaten op terugvalvolgorde.
export function candidateList(c: ImageCandidates): string[] {
  return [c.avif, c.jpeg, c.original];
}

export function isImageCandidates(source: unknown): source is ImageCandidates {
  return typeof source === "object" && source !== null && "avif" in source && "jpeg" in source && "original" in source;
}

// sourceThumbhash: de thumbhash van een bron uit imageCandidates, of
// undefined (geïmporteerde assets, oude backend).
export function sourceThumbhash(source: unknown): string | undefined {
  const t = typeof source === "object" && source !== null ? (source as { thumbhash?: unknown }).thumbhash : undefined;
  return typeof t === "string" && t !== "" ? t : undefined;
}
